import React, { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, IconButton } from "@mui/material";
import { IoClose } from "react-icons/io5";

const SlippageSettings = ({ open, setOpen, slippage, setSlippage }) => {
    const [customSlippage, setCustomSlippage] = useState("");
    const [activePreset, setActivePreset] = useState(null);
    const presets = [0.5, 1, 2];

    useEffect(() => {
        // Check if current slippage matches a preset
        if (presets.includes(slippage)) {
            setActivePreset(slippage);
            setCustomSlippage("");
        } else {
            setActivePreset(null);
            setCustomSlippage(slippage.toString());
        }
    }, [slippage]);

    const handlePresetClick = (preset) => {
        setSlippage(preset);
        setActivePreset(preset);
        setCustomSlippage("");
        // Save to localStorage
        localStorage.setItem("perpx_slippage", preset.toString());
    };

    const handleCustomChange = (value) => {
        // Allow empty string for clearing the input
        if (value === "") {
            setCustomSlippage("");
            return;
        }

        const numValue = parseFloat(value);

        // Only update if it's a valid number within range
        if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
            setCustomSlippage(value);
            setSlippage(numValue);
            setActivePreset(null);
            // Save to localStorage
            localStorage.setItem("perpx_slippage", numValue.toString());
        }
        // If value is out of range, don't update the input field
    };

    return (
        <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>
                Settings
                <IconButton
                    aria-label="close"
                    onClick={() => setOpen(false)}
                    sx={{ position: "absolute", right: 8, top: 8 }}
                >
                    <IoClose />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <div className="py-2">
                    <h3 className="text-perpx-cyan text-[16px] font-semibold mb-3">
                        Slippage Tolerance
                    </h3>
                    <p className="text-perpx-text-secondary text-[14px] mb-4">
                        Your transaction will revert if the price changes unfavorably by more than this percentage.
                    </p>

                    {/* Preset Buttons */}
                    <div className="flex gap-3 mb-4">
                        {presets.map((preset) => (
                            <button
                                key={preset}
                                onClick={() => handlePresetClick(preset)}
                                className={`flex-1 py-3 px-4 rounded-lg font-semibold text-[16px] transition-all ${
                                    activePreset === preset
                                        ? "bg-perpx-cyan text-perpx-dark"
                                        : "bg-perpx-dark-secondary text-perpx-text-primary border border-perpx-cyan/30 hover:border-perpx-cyan/50"
                                }`}
                            >
                                {preset}%
                            </button>
                        ))}
                    </div>

                    {/* Custom Input */}
                    <div className="relative">
                        <input
                            type="number"
                            min="0"
                            max="50"
                            step="0.1"
                            value={customSlippage}
                            onChange={(e) => handleCustomChange(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                                    e.preventDefault();
                                }
                            }}
                            placeholder="Custom"
                            className="w-full py-3 px-4 rounded-lg bg-perpx-dark-secondary text-perpx-text-primary border border-perpx-cyan/30 focus:border-perpx-cyan outline-none text-[16px] font-semibold"
                        />
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-perpx-text-secondary text-[16px]">
                            %
                        </span>
                    </div>

                    {/* Warning Messages */}
                    {slippage > 0 && slippage < 0.5 && (
                        <div className="mt-3 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                            <p className="text-yellow-400 text-[14px]">
                                ⚠️ Your transaction may fail due to low slippage tolerance
                            </p>
                        </div>
                    )}
                    {slippage > 5 && (
                        <div className="mt-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                            <p className="text-red-400 text-[14px]">
                                ⚠️ High slippage tolerance! You may receive less than expected
                            </p>
                        </div>
                    )}
                    {slippage >= 0.5 && slippage <= 5 && (
                        <div className="mt-3 p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                            <p className="text-green-400 text-[14px]">
                                ✓ Good slippage tolerance
                            </p>
                        </div>
                    )}

                    {/* Current Value Display */}
                    <div className="mt-4 p-3 rounded-lg bg-perpx-dark-secondary/50">
                        <p className="text-perpx-text-secondary text-[12px]">
                            Current slippage tolerance:{" "}
                            <span className="text-perpx-cyan font-semibold text-[14px]">
                                {slippage}%
                            </span>
                        </p>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default SlippageSettings;
